import React from "react";
import MaterialIcon from "../MaterialIcon";
import "./chat-pro.css";

// Bars of the mobile Pro composer block (v2/pro/ChatComposerPro.js):
//   ComposerContextBar  reply / edit bar on top of the composer: icon · 3px accent rule ·
//                       "Reply to Lisa" / "Edit message" · one-line snippet · ×
//   RecordingBar        delete · red pill (blinking dot, timer, live bars) · send
// plus replyPreviewLine (ReplyBarsGlass.replyPreviewLine): the one line a quote shows.

const stripToLine = (text) =>
  String(text || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/@\[([^\]]+)\]\([^)]*\)/g, "@$1")
    .replace(/(\*\*|__|~~|`+)/g, "")
    .replace(/(^|\s)[*_](\S)/g, "$1$2")
    .replace(/(\S)[*_](?=\s|$)/g, "$1")
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * One plain line for a quoted message: its text, or the type
 * ("Photo", "Video", "Voice message", file name, "Location", "Contact", "Poll").
 * `plain` = the decrypted text (may be empty).
 */
export function replyPreviewLine(chat, plain, labels = {}) {
  const c = chat || {};
  let text = stripToLine(plain);
  if (text.startsWith("U2FsdGVkX1")) text = "";
  switch (c.messageType) {
    case "image":
      return text || labels.photo;
    case "video":
      return text || labels.video;
    case "audio":
      return labels.voice;
    case "doc":
      return (Array.isArray(c.attachedFileName) ? c.attachedFileName[0] : c.attachedFileName) || labels.document;
    case "map":
      return labels.location;
    case "contact":
      return (c.contacts && c.contacts[0] && c.contacts[0].fullName) || labels.contact;
    case "poll":
      return labels.poll;
    default:
      return text;
  }
}

export function ComposerContextBar({ kind = "reply", title, text, onClose, closeLabel }) {
  return (
    <div className={`kjc-ctxbar kjc-ctxbar--${kind}`}>
      <MaterialIcon icon={kind === "edit" ? "edit" : "reply"} size={22} weight={300} className="kjc-ctxbar__icon" />
      <span className="kjc-ctxbar__rule" />
      <div className="kjc-ctxbar__text">
        <div className="kjc-ctxbar__title">{title}</div>
        {text ? <div className="kjc-ctxbar__snippet">{text}</div> : null}
      </div>
      <button
        type="button"
        className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kjc-ctxbar__close"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onClose) onClose(e);
        }}
        aria-label={closeLabel}
        title={closeLabel}
      >
        <MaterialIcon icon="close" size={20} weight={300} />
      </button>
    </div>
  );
}

/** "0:05" from seconds. */
export const fmtClock = (sec) => {
  const n = Math.max(0, Math.floor(Number(sec) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

const BARS = [6, 10, 14, 9, 18, 22, 15, 11, 19, 24, 17, 12, 8, 14, 20, 23, 16, 10, 7, 12, 18, 21, 14, 9, 13, 17, 11, 8, 12, 6];

export function RecordingBar({ seconds = 0, onCancel, onSend, cancelLabel, sendLabel }) {
  return (
    <div className="kjc-rec">
      <button
        type="button"
        className="kj-btn kj-btn--icon kj-btn--lg kj-btn--danger kjc-rec__cancel"
        onClick={(e) => {
          e.preventDefault();
          if (onCancel) onCancel();
        }}
        aria-label={cancelLabel}
        title={cancelLabel}
      >
        <MaterialIcon icon="delete" size={24} weight={300} />
      </button>
      <div className="kjc-rec__pill">
        <span className="kjc-rec__dot" />
        <span className="kjc-rec__time">{fmtClock(seconds)}</span>
        <span className="kjc-rec__wave" aria-hidden="true">
          {BARS.map((h, i) => (
            <span key={i} className="kjc-rec__bar" style={{ height: Math.round(3 + (h / 24) * 17), animationDelay: `${(i % 10) * 90}ms` }} />
          ))}
        </span>
      </div>
      <button
        type="button"
        className="kjc-round"
        onClick={(e) => {
          e.preventDefault();
          if (onSend) onSend();
        }}
        aria-label={sendLabel}
        title={sendLabel}
      >
        <MaterialIcon icon="send" size={22} weight={400} fill={1} />
      </button>
    </div>
  );
}
